import React, { CSSProperties, useEffect, useRef, useState } from 'react'

import styled from 'styled-components'

import {
  fetchZones,
  createZone,
  deleteZone,
  addPointToZone,
  removePointFromZone,
  IZone,
} from '../../../../../api/zones'
import iconPlus from '../../../../../assets/images/icon_plus_gray.svg'
import iconTrash from '../../../../../assets/images/icon_trash.svg'
import { useBuildingContext } from '../../../../../contexts/buildingContext'
import { useNotifications } from '../../../../../contexts/notificationProvider'
import { ViewSelector } from '../../../../common/ViewSelector/ViewSelector'
import { FloorPlanView } from '../../../building_page/components/FloorPlanView'

import { ZoneMapPoint, IZoneMapPoint } from './ZoneMapPoint'

const ViewSelectorContainer = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const ZonesContent = styled.div`
  padding: 0;
`

const CreateZoneContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 10px;
`

const CreateZoneInput = styled.input`
  border: none;
  font-size: 14px;
  flex: 1;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #212121;
  }
`

const ZoneList = styled.div`
  margin-top: 0;
`

const ZoneItem = styled.div<{ isActive?: boolean }>`
  padding: 10px;
  margin: 0;
  background: ${props => (props.isActive ? '#e6f2ff' : 'transparent')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border-bottom: 1px solid #e2e8f0;

  &:hover {
    background: ${props => (props.isActive ? '#e6f2ff' : '#f5f5f5')};
  }
`

const ZoneName = styled.span`
  color: #686868;
  font-size: 14px;
`

const PointCount = styled.span`
  color: #666;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
`

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 10px;
`

const TrashIcon = styled.img`
  cursor: pointer;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`

export const ManageZones = () => {
  const { state: buildingState } = useBuildingContext()
  const { addNotification } = useNotifications()
  const [zones, setZones] = useState<IZone[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [newZoneName, setNewZoneName] = useState<string>('')
  const [placeholder, setPlaceholder] = useState<string>('Create New Zone')
  const [zonesExpanded, setZonesExpanded] = useState<boolean>(true)
  const [activeZoneId, setActiveZoneId] = useState<number | null>(null)
  const [hoveredZoneId, setHoveredZoneId] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const loadZones = async () => {
      try {
        setLoading(true)
        const zonesData = await fetchZones(
          buildingState.projectId,
          buildingState.floorId,
        )
        setZones(zonesData)
        setError(null)
      } catch (err) {
        // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
        console.error('Error loading zones:', err)
        setError('Failed to load zones')
      } finally {
        setLoading(false)
      }
    }

    if (buildingState.projectId && buildingState.floorId) {
      loadZones()
    }
  }, [buildingState.projectId, buildingState.floorId])

  const onCreateNewZone = async () => {
    if (!newZoneName || !newZoneName.trim()) {
      return
    }

    try {
      const createdZone = await createZone(
        buildingState.projectId,
        buildingState.floorId,
        newZoneName,
      )

      setZones([...zones, createdZone])
      setNewZoneName('')
      inputRef.current?.blur()
      addNotification('Zone Created Successfully', 'success')
    } catch (err) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.error('Error creating zone:', err)
      addNotification('Zone Could Not Be Created', 'error')
    }
  }

  const onPressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onCreateNewZone()
    }
  }

  const onClickPlus = () => {
    if (!!newZoneName && !!newZoneName.trim()) {
      onCreateNewZone()
    } else {
      inputRef.current?.focus()
    }
  }

  const onDeleteZone = async (zoneId: number) => {
    try {
      await deleteZone(buildingState.projectId, buildingState.floorId, zoneId)
      setZones(zones.filter(zone => zone.id !== zoneId))
      if (activeZoneId === zoneId) {
        setActiveZoneId(null)
      }
      addNotification('Zone Deleted Successfully', 'success')
    } catch (err) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.error('Error deleting zone:', err)
      addNotification('Zone Could Not Be Deleted', 'error')
    }
  }

  const onClickZone = (zoneId: number) => {
    setActiveZoneId(activeZoneId === zoneId ? null : zoneId)
  }

  const onMouseEnterZone = (zoneId: number) => {
    setHoveredZoneId(zoneId)
  }

  const onMouseLeaveZone = () => {
    setHoveredZoneId(null)
  }

  const onClickPoint = async (
    e: React.MouseEvent<HTMLDivElement>,
    point: IZoneMapPoint,
  ) => {
    if (activeZoneId === null) {
      return
    }

    const activeZone = zones.find(z => z.id === activeZoneId)
    if (!activeZone) {
      return
    }

    // Check if point is already in the active zone (toggle behavior)
    const isInZone = activeZone.point_ids.includes(point.id)

    try {
      if (isInZone) {
        // Remove point from zone
        await removePointFromZone(
          buildingState.projectId,
          buildingState.floorId,
          activeZoneId,
          point.id,
        )

        // Update zones list by removing point from active zone
        setZones(
          zones.map(z =>
            z.id === activeZoneId
              ? { ...z, point_ids: z.point_ids.filter(id => id !== point.id) }
              : z,
          ),
        )
        addNotification('Point removed from zone', 'success')
      } else {
        // Add point to zone
        const updatedZone = await addPointToZone(
          buildingState.projectId,
          buildingState.floorId,
          activeZoneId,
          point.id,
        )

        // Update zones list with the updated zone
        setZones(zones.map(z => (z.id === activeZoneId ? updatedZone : z)))
        addNotification('Point added to zone', 'success')
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper error type
    } catch (err: any) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.error('Error modifying zone:', err)

      // Check for cross-zone assignment error
      if (!isInZone && err.response?.data?.data?.error) {
        const errorMsg = err.response.data.data.error
        if (errorMsg.includes('Point already assigned to:')) {
          addNotification(errorMsg, 'warning')
          return
        }
      }

      addNotification(
        isInZone
          ? 'Could not remove point from zone'
          : 'Could not add point to zone',
        'error',
      )
    }
  }

  const getActiveZonePointIds = (): Set<number> => {
    if (activeZoneId === null) return new Set()
    const activeZone = zones.find(z => z.id === activeZoneId)
    return activeZone ? new Set(activeZone.point_ids) : new Set()
  }

  const getHoveredZonePointIds = (): Set<number> => {
    if (hoveredZoneId === null) return new Set()
    const hoveredZone = zones.find(z => z.id === hoveredZoneId)
    return hoveredZone ? new Set(hoveredZone.point_ids) : new Set()
  }

  const activeZonePoints = getActiveZonePointIds()
  const hoveredZonePoints = getHoveredZonePointIds()

  // Safety check: ensure floorData and points exist
  if (!buildingState.floorData || !buildingState.floorData.points) {
    return <div>Loading floor data...</div>
  }

  const points = buildingState.floorData.points.map((point: IZoneMapPoint) => (
    <ZoneMapPoint
      key={point.point_id}
      point={point}
      x={parseFloat(point.x)}
      y={parseFloat(point.y)}
      isInActiveZone={activeZonePoints.has(point.id)}
      isInHoveredZone={hoveredZonePoints.has(point.id)}
      onClick={onClickPoint}
    />
  ))

  const zoomIconStyle: CSSProperties = {
    position: 'absolute',
    right: '0px',
    zIndex: 2,
  }
  const zoomInIconStyle: CSSProperties = { ...zoomIconStyle, bottom: '55px' }
  const zoomOutIconStyle: CSSProperties = { ...zoomIconStyle, bottom: '10px' }

  return (
    <>
      <FloorPlanView
        floorData={buildingState.floorData}
        hideDateRangeSelector
        mapViewerChildren={points}
        zoomInIconStyle={zoomInIconStyle}
        zoomOutIconStyle={zoomOutIconStyle}
        showPoints={false}
      />
      <ViewSelectorContainer>
        <ViewSelector
          title="Zones"
          initialViewSelectorExpanded
          viewingExpanded={zonesExpanded}
          setViewingExpanded={setZonesExpanded}
          maxHeight={400}
        >
          <ZonesContent>
            <CreateZoneContainer>
              <img
                alt="Create New Zone"
                src={iconPlus}
                style={{
                  cursor: 'pointer',
                  marginRight: '10px',
                  verticalAlign: 'middle',
                }}
                onClick={onClickPlus}
              />
              <CreateZoneInput
                ref={inputRef}
                placeholder={placeholder}
                value={newZoneName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewZoneName(e.target.value)
                }
                onFocus={() => setPlaceholder('')}
                onBlur={() => setPlaceholder('Create New Zone')}
                onKeyDown={onPressEnter}
              />
            </CreateZoneContainer>

            {loading && <p style={{ padding: '10px' }}>Loading zones...</p>}
            {error && <p style={{ color: 'red', padding: '10px' }}>{error}</p>}
            {!loading && !error && (
              <ZoneList>
                {zones.length === 0 ? (
                  <p style={{ padding: '10px' }}>No zones found.</p>
                ) : (
                  zones.map(zone => (
                    <ZoneItem
                      key={zone.id}
                      isActive={activeZoneId === zone.id}
                      onClick={() => onClickZone(zone.id)}
                      onMouseEnter={() => onMouseEnterZone(zone.id)}
                      onMouseLeave={onMouseLeaveZone}
                    >
                      <ZoneName>{zone.name}</ZoneName>
                      <RightContainer>
                        <PointCount>
                          {zone.point_ids.length}{' '}
                          {zone.point_ids.length === 1 ? 'point' : 'points'}
                        </PointCount>
                        <TrashIcon
                          src={iconTrash}
                          alt="Delete zone"
                          onClick={e => {
                            e.stopPropagation()
                            onDeleteZone(zone.id)
                          }}
                        />
                      </RightContainer>
                    </ZoneItem>
                  ))
                )}
              </ZoneList>
            )}
          </ZonesContent>
        </ViewSelector>
      </ViewSelectorContainer>
    </>
  )
}
